import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useTheme } from '../components/ThemeContext';
import { useResponsive } from '../components/useResponsive';
import { API_BASE } from '../../config';
import { useNavigate } from 'react-router-dom';

// ============================================================
// TYPES
// ============================================================

interface SwarmAgent {
  agent_id: string;
  name: string;
  enabled: boolean;
  last_run_at: string | null;
  last_run_status: string;
  products_processed_last_run: number;
  error_count_last_run: number;
  total_products_processed: number;
  next_scheduled_run: string | null;
}

interface SwarmStatus {
  service: string;
  status: string;
  agents: SwarmAgent[];
  total_products_scored: number;
  total_runs_today: number;
  uptime_seconds: number;
  alerts: Array<{ id: string; severity: string; message: string; fired_at: string }>;
}

interface BatchProgress {
  active_batch: string | null;
  total_items: number;
  completed_items: number;
  failed_items: number;
  progress_pct: number;
}

interface AgentHealth {
  status: string;
  last_heartbeat: string | null;
  error_rate: number;
  circuit_breaker_state: string;
  consecutive_failures: number;
  products_processed_total: number;
}

interface HealthSummary {
  agents: Record<string, AgentHealth>;
  overall_status: string;
}

// ============================================================
// AGENT METADATA
// ============================================================

const AGENT_META: Record<string, { display: string; type: string }> = {
  coo_scoring: { display: 'COO Scoring Agent', type: 'Scores products on country of origin and confidence level' },
  qrs: { display: 'Quality Rating Agent', type: 'Evaluates quality using recalls, inspections, and adverse events' },
  pis: { display: 'Pricing Intelligence Agent', type: 'Aggregates NADAC, AWP, and formulary pricing data' },
  coors: { display: 'COORS Agent', type: 'Calculates composite country of origin risk scores' },
  pns: { display: 'Political Nexus Agent', type: 'Analyzes political connections across five risk categories' },
  ndc_lookup: { display: 'NDC Lookup Agent', type: 'Retrieves product metadata from the FDA NDC Directory' },
  steward_country_normalizer: { display: 'Normalizer', type: 'Rule-based data normalization (COO only for now). Zero LLM cost' },
};

const STEWARD_META: Record<string, { display: string; type: string }> = {
  // 9 Original stewards (extracted from coo_scoring)
  steward_fda_establishments: { display: 'FDA Data Dashboard', type: 'Queries FDA Data Dashboard API for inspection and classification records' },
  steward_openfda_ndc: { display: 'OpenFDA NDC', type: 'Enriches products with OpenFDA drug and NDC data' },
  steward_fda_import_refusals: { display: 'FDA Import Refusals', type: 'Monitors import alerts and refusal records' },
  steward_epa_tri: { display: 'EPA TRI', type: 'Tracks toxic release inventory and environmental data' },
  steward_fda_recalls: { display: 'FDA Recalls', type: 'Monitors recall events and safety signals' },
  steward_dailymed_spl: { display: 'DailyMed SPL', type: 'Extracts country of origin from structured product labels' },
  steward_package_ocr: { display: 'Package OCR', type: 'Uses vision AI to extract text from drug package labels' },
  steward_sec_edgar: { display: 'SEC EDGAR', type: 'Analyzes corporate filings for ownership structure' },
  // 4 New stewards (PR #319)
  steward_drugs_at_fda: { display: 'Drugs@FDA', type: 'Retrieves drug approval history and application data' },
  steward_fda_registration: { display: 'FDA Registration', type: 'Looks up site registrations and FEI numbers' },
  steward_who_prequalification: { display: 'WHO Prequalification', type: 'Checks WHO prequalification list for global quality' },
  steward_dmf_pharmacompass: { display: 'DMF PharmaCompass', type: 'Identifies API sources from drug master files' },
  // 12 Additional stewards (PR #322 — product, pricing, clinical, political, legislative, compliance)
  steward_fda_ndc_directory: { display: 'FDA NDC Directory', type: 'Fetches NDC product data and metadata from FDA' },
  steward_fda_drug_shortages: { display: 'FDA Drug Shortages', type: 'Tracks shortage status and resolution timelines' },
  steward_pricing: { display: 'Pricing (NADAC)', type: 'Collects national average drug acquisition costs' },
  steward_rxnorm: { display: 'RxNorm', type: 'Maps clinical drugs to therapeutic classifications' },
  steward_fda_label_api: { display: 'FDA Label API', type: 'Retrieves drug label content from FDA SPL data' },
  steward_haiku_spl: { display: 'Haiku SPL Analysis', type: 'Uses AI to extract country of origin from labels' },
  steward_legislation: { display: 'Legislation', type: 'Tracks pharmaceutical bills in Congress' },
  steward_fec_donations: { display: 'FEC Donations', type: 'Monitors political donations from FEC filings' },
  steward_lobbying: { display: 'Lobbying (Senate LDA)', type: 'Collects lobbying disclosures from Senate LDA' },
  steward_politician_holdings: { display: 'Politician Holdings', type: 'Tracks politician stock trades and disclosures' },
  steward_pac_contributions: { display: 'PAC Contributions', type: 'Monitors PAC spending and political action data' },
  steward_ccp_flags: { display: 'CCP Flags', type: 'Screens for China exposure and CCP risk indicators' },
  // EU GMP compliance steward
  steward_eudra_gmdp: { display: 'EudraGMDP', type: 'Queries EMA GMP Compliance Database for manufacturing site certificates' },
  // Data quality steward
  steward_country_normalizer: { display: 'Normalizer', type: 'Rule-based data normalization (COO only for now). Zero LLM cost' },
  steward_manufacturer_name_geo: { display: 'Manufacturer Geo', type: 'Detects Chinese geographic keywords in manufacturer names' },
  steward_manufacturer_overview: { display: 'Manufacturer Overview', type: 'Aggregates BOL trade-flow per manufacturer (HS29/HS30 × imp/exp) — FREE /insights' },
};

// ============================================================
// HELPERS
// ============================================================

function formatUptime(seconds: number): string {
  if (seconds < 60) return `${seconds}s`;
  if (seconds < 3600) return `${Math.floor(seconds / 60)}m`;
  if (seconds < 86400) return `${Math.floor(seconds / 3600)}h ${Math.floor((seconds % 3600) / 60)}m`;
  return `${Math.floor(seconds / 86400)}d ${Math.floor((seconds % 86400) / 3600)}h`;
}

function formatTimeAgo(iso: string | null): string {
  if (!iso) return 'Never';
  const t = new Date(iso).getTime();
  if (Number.isNaN(t)) return 'Unknown';
  const diffMs = Date.now() - t;
  const diffMin = Math.floor(diffMs / 60000);
  if (diffMin < 1) return 'Just now';
  if (diffMin < 60) return `${diffMin}m ago`;
  const diffH = Math.floor(diffMin / 60);
  if (diffH < 24) return `${diffH}h ago`;
  return `${Math.floor(diffH / 24)}d ago`;
}

function getStatusBadgeStyle(status: string, T: ReturnType<typeof useTheme>['T']): React.CSSProperties {
  const s = status.toLowerCase();
  if (s === 'running') return { background: '#F0FDF4', color: T.success, border: '1px solid #BBF7D0' };
  if (s === 'healthy' || s === 'complete') return { background: '#ECFDF5', color: T.success, border: '1px solid #BBF7D0' };
  if (s === 'idle') return { background: '#EFF6FF', color: '#2563EB', border: '1px solid #BFDBFE' };
  if (s === 'degraded' || s === 'rate_limited') return { background: '#FFFBEB', color: T.warning, border: '1px solid #FDE68A' };
  if (s === 'error' || s === 'failed') return { background: '#FEF2F2', color: T.danger, border: '1px solid #FECACA' };
  return { background: T.borderLight, color: T.textLight, border: `1px solid ${T.border}` };
}

interface ApiBudgetItem {
  api_name: string;
  display_name: string;
  total_requests: number;
  daily_limit: number | null;
  usage_pct: number;
}

interface BudgetSummary {
  date: string | null;
  apis: ApiBudgetItem[];
  total_requests: number;
  total_cost: number;
  alerts: Array<{ api_name: string; message: string }>;
}

// ============================================================
// COMPONENT
// ============================================================

export default function AgentActivityDashboard() {
  const { T } = useTheme();
  const { isMobile, isTablet } = useResponsive();
  const navigate = useNavigate();

  const [swarmStatus, setSwarmStatus] = useState<SwarmStatus | null>(null);
  const [batchProgress, setBatchProgress] = useState<BatchProgress | null>(null);
  const [healthSummary, setHealthSummary] = useState<HealthSummary | null>(null);
  const [budgetSummary, setBudgetSummary] = useState<BudgetSummary | null>(null);
  const [totalProducts, setTotalProducts] = useState<number>(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [lastRefresh, setLastRefresh] = useState<Date>(new Date());
  const hasLoadedOnce = useRef(false);

  // Rescore All Products state — per-agent maps
  const [rescoreRunning, setRescoreRunning] = useState<Record<string, boolean>>({});
  const [rescoreRunId, setRescoreRunId] = useState<Record<string, string | null>>({});
  const [rescoreProgress, setRescoreProgress] = useState<Record<string, {
    status: string; total_products: number; products_completed: number;
    products_failed: number; progress_pct: number;
  } | null>>({});

  // Country normalizer target selection (drives the dedicated /normalize-* endpoints)
  type NormalizerTarget = 'all' | 'products' | 'manufacturers' | 'api_source';
  const [normalizerTarget, setNormalizerTarget] = useState<NormalizerTarget>('all');
  // When 'all' is selected, after the manufacturers run completes the polling
  // effect dispatches a follow-up products run via this flag.
  const [normalizerPendingProducts, setNormalizerPendingProducts] = useState(false);

  // LLM Analysis Queue state — per-agent maps
  const [llmQueuePending, setLlmQueuePending] = useState<Record<string, number>>({});
  const [llmQueueCompleted, setLlmQueueCompleted] = useState<Record<string, number>>({});
  const [llmQueueRunning, setLlmQueueRunning] = useState<Record<string, boolean>>({});
  const [llmQueueRunId, setLlmQueueRunId] = useState<Record<string, string | null>>({});
  const [llmQueueProgress, setLlmQueueProgress] = useState<Record<string, {
    status: string; total: number; processed: number;
    failed: number; progress_pct: number;
  } | null>>({});

  // Per-steward data completeness %
  const [stewardCompleteness, setStewardCompleteness] = useState<Record<string, { total_products: number; completeness_pct: number }>>({});

  // Data Orchestrator state
  const [orchStatus, setOrchStatus] = useState<{
    collecting: boolean;
    stewards_available: number;
    last_collection?: { status: string; duration_seconds?: number; stewards_completed?: number; stewards_failed?: number; total_updated?: number; total_failed?: number };
  } | null>(null);

  // COO Confidence Score state
  const [cooConfidence, setCooConfidence] = useState<{ overall_high_pct: number; overall_mod_pct: number; overall_low_pct: number; total_scored: number; history: Array<{ date: string; products_scored: number; batch_high_pct: number; batch_mod_pct: number; batch_low_pct: number; overall_high_pct: number; overall_mod_pct: number; overall_low_pct: number }> } | null>(null);
  const [showCooHistory, setShowCooHistory] = useState(false);

  const fetchData = useCallback(async () => {
    try {
      const [statusRes, batchRes, healthRes, budgetRes, statsRes, orchRes] = await Promise.allSettled([
        fetch(`${API_BASE}/api/metasupply/swarm/status`, { credentials: 'include' }),
        fetch(`${API_BASE}/api/metasupply/swarm/batch/progress`, { credentials: 'include' }),
        fetch(`${API_BASE}/api/metasupply/swarm/health`, { credentials: 'include' }),
        fetch(`${API_BASE}/api/metasupply/swarm/budget/summary`, { credentials: 'include' }),
        fetch(`${API_BASE}/metasupply/dashboard/stats`, { credentials: 'include' }),
        fetch(`${API_BASE}/api/metasupply/swarm/orchestrator/status`, { credentials: 'include' }),
      ]);

      if (statusRes.status === 'fulfilled' && statusRes.value.ok) {
        setSwarmStatus(await statusRes.value.json());
      }
      if (batchRes.status === 'fulfilled' && batchRes.value.ok) {
        setBatchProgress(await batchRes.value.json());
      }
      if (healthRes.status === 'fulfilled' && healthRes.value.ok) {
        setHealthSummary(await healthRes.value.json());
      }
      if (budgetRes.status === 'fulfilled' && budgetRes.value.ok) {
        setBudgetSummary(await budgetRes.value.json());
      }
      if (statsRes.status === 'fulfilled' && statsRes.value.ok) {
        const statsData = await statsRes.value.json();
        if (statsData.total_products && statsData.total_products > 0) {
          setTotalProducts(statsData.total_products);
        }
      }
      if (orchRes.status === 'fulfilled' && orchRes.value.ok) {
        setOrchStatus(await orchRes.value.json());
      }

      setError(null);
      setLastRefresh(new Date());
      hasLoadedOnce.current = true;
    } catch (e: unknown) {
      if (!hasLoadedOnce.current) {
        setError(e instanceof Error ? e.message : 'Failed to fetch swarm data');
      }
    } finally {
      setLoading(false);
    }
  }, []);

  // Fetch steward data-completeness (deferred — not blocking initial render)
  const fetchCompleteness = useCallback(async () => {
    try {
      const resp = await fetch(`${API_BASE}/api/metasupply/swarm/stewards/data-completeness`, { credentials: 'include' });
      if (resp.ok) {
        setStewardCompleteness(await resp.json());
      }
    } catch {
      // ignore — completeness is non-critical
    }
  }, []);

  // Fetch COO confidence history
  const fetchCooConfidence = useCallback(async () => {
    try {
      const resp = await fetch(`${API_BASE}/api/metasupply/coo-confidence-history`, { credentials: 'include' });
      if (resp.ok) {
        setCooConfidence(await resp.json());
      }
    } catch {
      // ignore
    }
  }, []);

  // Restore active rescore-all runs from backend (survives page reload)
  const fetchActiveRescoreRuns = useCallback(async () => {
    try {
      const resp = await fetch(`${API_BASE}/api/metasupply/swarm/trigger-all`, { credentials: 'include' });
      if (resp.ok) {
        const data = await resp.json();
        const runs: Array<{ run_id: string; agent_id: string; status: string; total_products: number; products_completed: number; products_failed: number; progress_pct: number }> = data.runs || [];
        for (const run of runs) {
          if (run.status === 'running') {
            setRescoreRunning(prev => ({ ...prev, [run.agent_id]: true }));
            setRescoreRunId(prev => ({ ...prev, [run.agent_id]: run.run_id }));
            setRescoreProgress(prev => ({ ...prev, [run.agent_id]: {
              status: run.status,
              total_products: run.total_products,
              products_completed: run.products_completed,
              products_failed: run.products_failed,
              progress_pct: run.progress_pct,
            }}));
          }
        }
      }
    } catch {
      // ignore — backend may be busy
    }
  }, []);

  useEffect(() => {
    fetchData();
    fetchCompleteness();
    fetchCooConfidence();
    fetchActiveRescoreRuns();
    const interval = setInterval(() => { fetchData(); fetchCompleteness(); }, 60000);
    return () => clearInterval(interval);
  }, [fetchData, fetchCompleteness, fetchCooConfidence, fetchActiveRescoreRuns]);

  // Fetch LLM queue status for all scoring agents (parallel)
  const fetchLlmQueueStatus = useCallback(async () => {
    const agents = ['coo_scoring', 'pns', 'qrs'] as const;
    const results = await Promise.allSettled(
      agents.map(aid => fetch(`${API_BASE}/api/metasupply/swarm/llm-queue/status?agent_id=${aid}`, { credentials: 'include' }))
    );
    for (let i = 0; i < agents.length; i++) {
      const aid = agents[i];
      const res = results[i];
      if (res.status === 'fulfilled' && res.value.ok) {
        try {
          const data = await res.value.json();
          setLlmQueuePending(prev => ({ ...prev, [aid]: data.pending || 0 }));
          setLlmQueueCompleted(prev => ({ ...prev, [aid]: data.completed || 0 }));
          if (data.active_run) {
            setLlmQueueRunning(prev => ({ ...prev, [aid]: true }));
            setLlmQueueRunId(prev => ({ ...prev, [aid]: data.active_run.run_id }));
          }
        } catch {
          // ignore parse error
        }
      }
    }
  }, []);

  // Fetch LLM queue status on mount and periodically
  useEffect(() => {
    fetchLlmQueueStatus();
    const interval = setInterval(fetchLlmQueueStatus, 30000);
    return () => clearInterval(interval);
  }, [fetchLlmQueueStatus]);

  // Poll LLM queue run progress — per-agent
  useEffect(() => {
    const activeAgents = Object.entries(llmQueueRunId).filter(
      ([aid]) => llmQueueRunning[aid] && llmQueueRunId[aid],
    );
    if (activeAgents.length === 0) return;
    const pollInterval = setInterval(async () => {
      for (const [aid, runId] of activeAgents) {
        if (!runId) continue;
        try {
          const resp = await fetch(`${API_BASE}/api/metasupply/swarm/llm-queue/${runId}`, { credentials: 'include' });
          if (resp.ok) {
            const data = await resp.json();
            setLlmQueueProgress(prev => ({ ...prev, [aid]: {
              status: data.status,
              total: data.total,
              processed: data.processed,
              failed: data.failed,
              progress_pct: data.progress_pct,
            }}));
            if (data.status === 'completed' || data.status === 'stopped') {
              setLlmQueueRunning(prev => ({ ...prev, [aid]: false }));
              fetchLlmQueueStatus();
            }
          }
        } catch {
          // ignore
        }
      }
    }, 5000);
    return () => clearInterval(pollInterval);
  }, [llmQueueRunId, llmQueueRunning, fetchLlmQueueStatus]);

  // Poll rescore-all progress when any agent run is active
  useEffect(() => {
    const activeAgents = Object.entries(rescoreRunId).filter(
      ([aid]) => rescoreRunning[aid] && rescoreRunId[aid],
    );
    if (activeAgents.length === 0) return;
    const pollInterval = setInterval(async () => {
      for (const [aid, runId] of activeAgents) {
        if (!runId) continue;
        try {
          const resp = await fetch(`${API_BASE}/api/metasupply/swarm/trigger-all/${runId}`, { credentials: 'include' });
          if (resp.ok) {
            const data = await resp.json();
            setRescoreProgress(prev => ({ ...prev, [aid]: {
              status: data.status,
              total_products: data.total_products,
              products_completed: data.products_completed,
              products_failed: data.products_failed,
              progress_pct: data.progress_pct,
            }}));
            if (data.status === 'completed' || data.status === 'stopped') {
              // 'All' chain: when the manufacturers leg of a normalizer
              // run completes, immediately fire the products leg.
              if (
                aid === 'steward_country_normalizer' &&
                normalizerPendingProducts &&
                data.status === 'completed' &&
                data.target === 'manufacturers'
              ) {
                setNormalizerPendingProducts(false);
                try {
                  const r = await fetch(
                    `${API_BASE}/api/metasupply/swarm/agents/${aid}/normalize-products`,
                    {
                      method: 'POST',
                      credentials: 'include',
                      headers: { 'Content-Type': 'application/json' },
                      body: JSON.stringify({}),
                    },
                  );
                  if (r.ok) {
                    const d = await r.json();
                    setRescoreRunId(prev => ({ ...prev, [aid]: d.run_id }));
                    setRescoreProgress(prev => ({ ...prev, [aid]: {
                      status: 'running', total_products: 0,
                      products_completed: 0, products_failed: 0, progress_pct: 0,
                    }}));
                    continue;
                  }
                } catch {
                  // fall through to mark not running
                }
              }
              setRescoreRunning(prev => ({ ...prev, [aid]: false }));
              // Refresh dashboard data and LLM queue status
              fetchData();
              fetchLlmQueueStatus();
            }
          }
        } catch {
          // Polling error — ignore, will retry next interval
        }
      }
    }, 5000);
    return () => clearInterval(pollInterval);
  }, [rescoreRunId, rescoreRunning, fetchData, fetchLlmQueueStatus, normalizerPendingProducts]);

  // Orchestrator derived values
  const orchUpdated = orchStatus?.last_collection?.total_updated ?? 0;
  const orchFailed = orchStatus?.last_collection?.total_failed ?? 0;
  const orchLastStatus = orchStatus?.last_collection?.status ?? 'idle';
  const TOTAL_PRODUCTS = totalProducts || 129000;
  const overallStatus = swarmStatus?.status || (hasLoadedOnce.current ? 'offline' : 'connecting');
  const totalScored = swarmStatus?.total_products_scored ?? 0;
  const agents = swarmStatus?.agents || [];
  const stewardIds = new Set(Object.keys(STEWARD_META));
  const hiddenScoringIds = new Set(['pis', 'coors', 'ndc_lookup']);
  const hiddenStewardIds = new Set(['steward_legislation']);
  const manufacturerStewardIds = new Set(['steward_fec_donations', 'steward_lobbying', 'steward_politician_holdings', 'steward_pac_contributions', 'steward_ccp_flags', 'steward_manufacturer_name_geo', 'steward_manufacturer_overview']);
  const orchStewardsAvail = Object.keys(STEWARD_META).length - hiddenStewardIds.size;
  const scoringAgents = agents.filter(a => !stewardIds.has(a.agent_id) && !hiddenScoringIds.has(a.agent_id));
  const stewardAgents = agents.filter(a => stewardIds.has(a.agent_id));
  const visibleAgents = agents.filter(a => !hiddenScoringIds.has(a.agent_id) && !hiddenStewardIds.has(a.agent_id));
  const activeAgents = visibleAgents.filter(a => a.enabled).length;
  const totalAgents = (Object.keys(STEWARD_META).length - hiddenStewardIds.size) + (Object.keys(AGENT_META).length - hiddenScoringIds.size);
  const progressPct = batchProgress?.progress_pct ?? (TOTAL_PRODUCTS > 0 ? Math.round((totalScored / TOTAL_PRODUCTS) * 100) : 0);
  const completedItems = batchProgress?.completed_items ?? totalScored;
  const totalItems = batchProgress?.total_items || TOTAL_PRODUCTS;

  // SVG ring calculations
  const ringSize = 130;
  const ringStroke = 7;
  const ringR = (ringSize - ringStroke) / 2;
  const ringCirc = 2 * Math.PI * ringR;
  const healthPct = totalAgents > 0 ? activeAgents / totalAgents : 0;
  const ringOffset = ringCirc - healthPct * ringCirc;

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 300 }}>
        <div style={{ textAlign: 'center' }}>
          <div style={{ width: 40, height: 40, border: `3px solid ${T.border}`, borderTopColor: T.gold, borderRadius: '50%', animation: 'spin 1s linear infinite', margin: '0 auto 16px' }} />
          <span style={{ fontSize: 14, color: T.textLight }}>Loading Agent Swarm status...</span>
        </div>
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>

      {/* Hero Banner */}
      <div style={{
        position: 'relative',
        background: T.offWhite,
        borderRadius: 16, padding: isMobile ? '24px 16px' : '32px 40px', overflow: 'hidden',
        display: 'flex', flexDirection: isMobile ? 'column' : 'row',
        justifyContent: 'space-between', alignItems: isMobile ? 'flex-start' : 'center', gap: isMobile ? 24 : 32,
        border: `1px solid ${T.border}`,
      }}>

        {/* Left: title + description */}
        <div style={{ position: 'relative', zIndex: 1, flex: 1 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 14 }}>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke={T.red} strokeWidth="2">
              <circle cx="12" cy="12" r="3" />
              <path d="M12 1v2M12 21v2M4.22 4.22l1.42 1.42M18.36 18.36l1.42 1.42M1 12h2M21 12h2M4.22 19.78l1.42-1.42M18.36 5.64l1.42-1.42" />
            </svg>
            <span style={{ fontSize: 11, fontWeight: 700, color: T.red, textTransform: 'uppercase', letterSpacing: '0.15em', fontFamily: "'IBM Plex Mono', 'Courier New', monospace" }}>
              Agent Swarm Command
            </span>
          </div>
          <h1 style={{ fontSize: isMobile ? 22 : 28, fontWeight: 800, color: T.textDark, letterSpacing: '-0.02em', lineHeight: 1.2, marginBottom: 8, fontFamily: "'Playfair Display', Georgia, serif" }}>
            Agent Activity<br />Dashboard
          </h1>
          <p style={{ fontSize: 14, color: T.textMid, maxWidth: 480, lineHeight: 1.6 }}>
            Real-time monitoring of {totalAgents} agents ({orchStewardsAvail} field agents, {scoringAgents.length || Object.keys(AGENT_META).length - hiddenScoringIds.size} analysts) processing {TOTAL_PRODUCTS.toLocaleString()}+ NDCs.
            Track health, progress, and data quality across the pharmaceutical intelligence pipeline.
          </p>
        </div>

        {/* Right: Active Agents Ring */}
        <div style={{ position: 'relative', zIndex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8, minWidth: isMobile ? undefined : 200 }}>
          <span style={{ fontSize: 10, fontWeight: 700, color: T.red, textTransform: 'uppercase', letterSpacing: '0.18em', fontFamily: "'IBM Plex Mono', 'Courier New', monospace" }}>
            Active Agents
          </span>
          <div style={{ position: 'relative', width: ringSize + 20, height: ringSize + 20 }}>
            {/* Decorative tick marks */}
            <svg width={ringSize + 20} height={ringSize + 20} style={{ position: 'absolute', inset: 0 }}>
              {Array.from({ length: 60 }).map((_, i) => {
                const angle = (i * 6) * Math.PI / 180;
                const cx = (ringSize + 20) / 2;
                const cy = (ringSize + 20) / 2;
                const outerR = (ringSize + 20) / 2 - 1;
                const isMajor = i % 5 === 0;
                const innerR = outerR - (isMajor ? 8 : 4);
                return (
                  <line
                    key={i}
                    x1={cx + innerR * Math.cos(angle)}
                    y1={cy + innerR * Math.sin(angle)}
                    x2={cx + outerR * Math.cos(angle)}
                    y2={cy + outerR * Math.sin(angle)}
                    stroke={isMajor ? T.red : T.border}
                    strokeWidth={isMajor ? 1.5 : 0.5}
                    opacity={isMajor ? 0.6 : 0.4}
                  />
                );
              })}
            </svg>
            {/* Main ring */}
            <svg width={ringSize} height={ringSize} style={{ transform: 'rotate(-90deg)', position: 'absolute', top: 10, left: 10 }}>
              <circle cx={ringSize / 2} cy={ringSize / 2} r={ringR} fill="none" stroke={T.border} strokeWidth={ringStroke} />
              <circle
                cx={ringSize / 2} cy={ringSize / 2} r={ringR}
                fill="none" stroke={overallStatus === 'offline' ? T.danger : overallStatus === 'connecting' ? T.warning : T.red} strokeWidth={ringStroke}
                strokeDasharray={ringCirc} strokeDashoffset={overallStatus === 'offline' || overallStatus === 'connecting' ? ringCirc : ringOffset}
                strokeLinecap="round"
                style={{ transition: 'stroke-dashoffset 1.2s ease, stroke 0.4s ease' }}
              />
            </svg>
            <div style={{
              position: 'absolute', inset: 10,
              display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
            }}>
              <span style={{ fontSize: 38, fontWeight: 900, color: T.textDark, lineHeight: 1 }}>
                {activeAgents}
              </span>
              <span style={{ fontSize: 13, fontWeight: 500, color: T.textMid, marginTop: 2 }}>
                of {totalAgents}
              </span>
            </div>
          </div>
          {/* Uptime pill */}
          <div style={{
            display: 'flex', alignItems: 'center', gap: 6,
            background: T.navy, borderRadius: 20, padding: '5px 16px',
          }}>
            <div style={{
              width: 6, height: 6, borderRadius: '50%',
              background: overallStatus === 'offline' ? T.danger : overallStatus === 'connecting' ? T.warning : T.success,
              animation: overallStatus === 'connecting' ? 'pulse 1s infinite' : overallStatus !== 'offline' ? 'pulse 2s infinite' : 'none',
            }} />
            <span style={{ fontSize: 11, fontWeight: 600, color: '#f2ede4' }}>
              {swarmStatus?.uptime_seconds != null && swarmStatus.uptime_seconds > 0
                ? `Uptime: ${formatUptime(swarmStatus.uptime_seconds)}`
                : overallStatus === 'offline' ? 'Service offline' : overallStatus === 'connecting' ? 'Connecting to swarm...' : 'Starting up...'
              }
            </span>
          </div>
        </div>
      </div>

      {/* Refresh bar */}
      <div style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 12 }}>
        {error && <span style={{ fontSize: 12, color: T.danger, marginRight: 'auto' }}>{error}</span>}
        <span style={{ fontSize: 11, color: T.textLight }}>Last updated: {lastRefresh.toLocaleTimeString()}</span>
        <button
          onClick={() => { setLoading(true); fetchData(); }}
          style={{
            padding: '6px 16px', borderRadius: T.radiusSm,
            border: `1px solid ${T.border}`, background: T.white,
            color: T.textDark, cursor: 'pointer', fontSize: 12,
            fontWeight: 600, fontFamily: 'inherit', transition: 'all 0.2s',
          }}
          onMouseEnter={e => { (e.currentTarget as HTMLButtonElement).style.borderColor = T.gold; }}
          onMouseLeave={e => { (e.currentTarget as HTMLButtonElement).style.borderColor = T.border; }}
        >
          Refresh
        </button>
      </div>

      {/* Stat Cards */}
      <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : isTablet ? 'repeat(2, 1fr)' : 'repeat(4, 1fr)', gap: 16 }}>
        {[
          {
            title: 'Total NDCs', value: TOTAL_PRODUCTS.toLocaleString(), subtitle: 'In processing universe',
            accent: `linear-gradient(90deg, ${T.navy}, ${T.navyLight})`,
            iconBg: '#EFF6FF', iconColor: T.navyLight, valueColor: T.textDark,
            icon: (
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <rect x="2" y="3" width="20" height="14" rx="2" /><path d="M8 21h8M12 17v4" />
              </svg>
            ),
          },
          {
            title: 'Processed Records', value: totalScored.toLocaleString(),
            subtitle: 'Total processing operations across all agents',
            accent: `linear-gradient(90deg, ${T.success}, #34D399)`,
            iconBg: '#ECFDF5', iconColor: T.success, valueColor: T.success,
            icon: (
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M20 6L9 17l-5-5" />
              </svg>
            ),
          },
          {
            title: 'Total Runs (Lifetime)', value: String(swarmStatus?.total_runs_today ?? 0),
            subtitle: 'Cumulative agent executions',
            accent: `linear-gradient(90deg, ${T.warning}, #FBBF24)`,
            iconBg: '#FFFBEB', iconColor: T.warning, valueColor: T.textDark,
            icon: (
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <circle cx="12" cy="12" r="10" /><path d="M12 6v6l4 2" />
              </svg>
            ),
          },
          {
            title: 'COO Confidence',
            value: cooConfidence ? `${cooConfidence.overall_high_pct}%` : '—',
            subtitle: cooConfidence ? `HIGH tier across ${cooConfidence.total_scored.toLocaleString()} products` : 'Loading...',
            accent: `linear-gradient(90deg, ${T.gold}, #F59E0B)`,
            iconBg: '#FFFBEB', iconColor: T.gold, valueColor: T.textDark,
            clickable: true,
            icon: (
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M22 12h-4l-3 9L9 3l-3 9H2" />
              </svg>
            ),
          },
        ].map(card => (
          <div key={card.title}
            onClick={'clickable' in card && card.clickable ? () => setShowCooHistory(prev => !prev) : undefined}
            style={{
              background: T.white, border: `1px solid ${'clickable' in card && card.clickable && showCooHistory ? T.gold : T.border}`, borderRadius: T.radius,
              padding: 20, boxShadow: T.shadow, position: 'relative', overflow: 'hidden',
              cursor: 'clickable' in card && card.clickable ? 'pointer' : 'default',
              transition: 'all 0.15s',
          }}>
            <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: 3, background: card.accent }} />
            <div style={{
              width: 36, height: 36, borderRadius: 10, display: 'flex', alignItems: 'center', justifyContent: 'center',
              marginBottom: 12, background: card.iconBg, color: card.iconColor,
            }}>
              {card.icon}
            </div>
            <div style={{ fontSize: 11, fontWeight: 700, color: T.textLight, textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: 6 }}>
              {card.title}
            </div>
            <div style={{ fontSize: 28, fontWeight: 800, color: card.valueColor, lineHeight: 1, marginBottom: 4 }}>
              {card.value}
            </div>
            <div style={{ fontSize: 11, color: T.textLight }}>{card.subtitle}</div>
          </div>
        ))}
      </div>

      {/* COO Confidence Score History (shown when tile clicked) */}
      {showCooHistory && cooConfidence && (
        <div style={{
          background: T.white, border: `1px solid ${T.gold}`, borderRadius: T.radius,
          padding: isMobile ? 16 : 24, boxShadow: T.shadow,
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
            <div>
              <div style={{ fontSize: 16, fontWeight: 700, color: T.navy }}>COO Confidence Score History</div>
              <div style={{ fontSize: 12, color: T.textLight, marginTop: 2 }}>
                Track confidence tier distribution per run and across the full product universe
              </div>
            </div>
            <button
              onClick={() => setShowCooHistory(false)}
              style={{
                padding: '6px 14px', borderRadius: 6, border: `1px solid ${T.border}`,
                background: T.offWhite, color: T.textMid, fontSize: 12, fontWeight: 600,
                cursor: 'pointer', fontFamily: 'inherit',
              }}
            >Close</button>
          </div>

          {cooConfidence.history.length === 0 ? (
            <div style={{ padding: 40, textAlign: 'center', color: T.textLight, fontSize: 14 }}>
              No historical COO confidence data available yet.
            </div>
          ) : (
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr>
                    <th rowSpan={2} style={{ padding: '10px 12px', textAlign: 'left', fontSize: 10, fontWeight: 700, color: T.textLight, textTransform: 'uppercase', letterSpacing: '0.08em', verticalAlign: 'bottom', borderBottom: `2px solid ${T.border}` }}>Date</th>
                    <th rowSpan={2} style={{ padding: '10px 12px', textAlign: 'center', fontSize: 10, fontWeight: 700, color: T.textLight, textTransform: 'uppercase', letterSpacing: '0.08em', verticalAlign: 'bottom', borderBottom: `2px solid ${T.border}` }}>Count</th>
                    <th colSpan={3} style={{ padding: '6px 12px', textAlign: 'center', fontSize: 11, fontWeight: 700, color: T.navy, letterSpacing: '0.06em', background: T.offWhite, borderBottom: `2px solid ${T.gold}`, borderTop: `2px solid ${T.gold}` }}>Run Confidence</th>
                    <th colSpan={3} style={{ padding: '6px 12px', textAlign: 'center', fontSize: 11, fontWeight: 700, color: T.navy, letterSpacing: '0.06em', background: T.offWhite, borderBottom: `2px solid ${T.navy}`, borderTop: `2px solid ${T.navy}` }}>Overall Confidence</th>
                  </tr>
                  <tr>
                    <th style={{ padding: '8px 12px', textAlign: 'center', fontSize: 10, fontWeight: 700, color: '#16a34a', textTransform: 'uppercase', letterSpacing: '0.08em', borderBottom: `2px solid ${T.border}` }}>High</th>
                    <th style={{ padding: '8px 12px', textAlign: 'center', fontSize: 10, fontWeight: 700, color: '#d97706', textTransform: 'uppercase', letterSpacing: '0.08em', borderBottom: `2px solid ${T.border}` }}>Mod</th>
                    <th style={{ padding: '8px 12px', textAlign: 'center', fontSize: 10, fontWeight: 700, color: '#dc2626', textTransform: 'uppercase', letterSpacing: '0.08em', borderBottom: `2px solid ${T.border}` }}>Low</th>
                    <th style={{ padding: '8px 12px', textAlign: 'center', fontSize: 10, fontWeight: 700, color: '#16a34a', textTransform: 'uppercase', letterSpacing: '0.08em', borderBottom: `2px solid ${T.border}` }}>High</th>
                    <th style={{ padding: '8px 12px', textAlign: 'center', fontSize: 10, fontWeight: 700, color: '#d97706', textTransform: 'uppercase', letterSpacing: '0.08em', borderBottom: `2px solid ${T.border}` }}>Mod</th>
                    <th style={{ padding: '8px 12px', textAlign: 'center', fontSize: 10, fontWeight: 700, color: '#dc2626', textTransform: 'uppercase', letterSpacing: '0.08em', borderBottom: `2px solid ${T.border}` }}>Low</th>
                  </tr>
                </thead>
                <tbody>
                  {cooConfidence.history.map((entry, idx) => (
                    <tr key={entry.date} style={{
                      borderBottom: idx < cooConfidence.history.length - 1 ? `1px solid ${T.borderLight}` : 'none',
                    }}>
                      <td style={{ padding: '12px 12px', fontSize: 13, fontWeight: 600, color: T.textDark }}>
                        {new Date(entry.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                      </td>
                      <td style={{ padding: '12px 12px', textAlign: 'center', fontSize: 13, color: T.textMid }}>
                        {entry.products_scored.toLocaleString()}
                      </td>
                      {/* Batch Confidence: High / Moderate / Low */}
                      <td style={{ padding: '12px 12px', textAlign: 'center', fontSize: 13, fontWeight: 700, color: '#16a34a' }}>{entry.batch_high_pct}%</td>
                      <td style={{ padding: '12px 12px', textAlign: 'center', fontSize: 13, fontWeight: 700, color: '#d97706' }}>{entry.batch_mod_pct}%</td>
                      <td style={{ padding: '12px 12px', textAlign: 'center', fontSize: 13, fontWeight: 700, color: '#dc2626' }}>{entry.batch_low_pct}%</td>
                      {/* Overall Confidence: High / Moderate / Low — show "--" when not available */}
                      <td style={{ padding: '12px 12px', textAlign: 'center', fontSize: 13, fontWeight: 700, color: entry.overall_high_pct != null ? '#16a34a' : T.textMid }}>{entry.overall_high_pct != null ? `${entry.overall_high_pct}%` : '--'}</td>
                      <td style={{ padding: '12px 12px', textAlign: 'center', fontSize: 13, fontWeight: 700, color: entry.overall_mod_pct != null ? '#d97706' : T.textMid }}>{entry.overall_mod_pct != null ? `${entry.overall_mod_pct}%` : '--'}</td>
                      <td style={{ padding: '12px 12px', textAlign: 'center', fontSize: 13, fontWeight: 700, color: entry.overall_low_pct != null ? '#dc2626' : T.textMid }}>{entry.overall_low_pct != null ? `${entry.overall_low_pct}%` : '--'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* ══════════════════════════════════════════════════════════
          DATA ORCHESTRATOR — full-width tile above stewards
          ══════════════════════════════════════════════════════════ */}
      <div
        style={{
          background: T.offWhite,
          border: `1px solid ${T.border}`, borderRadius: T.radius,
          padding: isMobile ? 20 : 28, boxShadow: T.shadowMd,
          position: 'relative', overflow: 'hidden',
          cursor: 'pointer', transition: 'transform 0.15s, box-shadow 0.15s',
        }}
        onClick={() => navigate('/agent-detail/data_orchestrator')}
        onMouseEnter={e => {
          (e.currentTarget as HTMLDivElement).style.transform = 'translateY(-2px)';
          (e.currentTarget as HTMLDivElement).style.boxShadow = '0 8px 30px rgba(0,0,0,0.18)';
        }}
        onMouseLeave={e => {
          (e.currentTarget as HTMLDivElement).style.transform = 'translateY(0)';
          (e.currentTarget as HTMLDivElement).style.boxShadow = T.shadowMd;
        }}
      >
        {/* Accent bar */}
        <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: 3, background: `linear-gradient(90deg, ${T.red}, ${T.redLight}, ${T.red})` }} />

        <div style={{
          display: 'flex', flexDirection: isMobile ? 'column' : 'row',
          justifyContent: 'space-between', alignItems: isMobile ? 'flex-start' : 'center',
          gap: isMobile ? 20 : 32, position: 'relative', zIndex: 1,
        }}>
          {/* Left: title + description */}
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10 }}>
              <div style={{
                width: 32, height: 32, borderRadius: 8,
                background: `linear-gradient(135deg, ${T.red}, ${T.redLight})`,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
              }}>
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2.5">
                  <circle cx="12" cy="12" r="3" />
                  <path d="M12 1v2M12 21v2M4.22 4.22l1.42 1.42M18.36 18.36l1.42 1.42M1 12h2M21 12h2M4.22 19.78l1.42-1.42M18.36 5.64l1.42-1.42" />
                </svg>
              </div>
              <div>
                <span style={{ fontSize: 11, fontWeight: 700, color: T.gold, textTransform: 'uppercase', letterSpacing: '0.15em' }}>
                  Case Officer
                </span>
                <span style={{
                  fontSize: 8, fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.04em',
                  padding: '2px 8px', borderRadius: 12, marginLeft: 10,
                  background: orchStatus?.collecting ? 'rgba(34,197,94,0.2)' : orchLastStatus === 'completed' ? 'rgba(34,197,94,0.15)' : `${T.navy}10`,
                  color: orchStatus?.collecting ? '#22C55E' : orchLastStatus === 'completed' ? '#22C55E' : T.textLight,
                }}>
                  {orchStatus?.collecting ? 'Running' : orchLastStatus === 'completed' ? 'Idle' : orchLastStatus}
                </span>
              </div>
            </div>
            <p style={{ fontSize: 13, color: T.textMid, maxWidth: 520, lineHeight: 1.6, margin: 0, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
              Coordinates {orchStewardsAvail} field agents and {scoringAgents.length || Object.keys(AGENT_META).length - hiddenScoringIds.size} analyst agents. Click to view details.
            </p>
          </div>

          {/* Right: metrics cards */}
          <div style={{
            display: 'flex', gap: 16, flexShrink: 0,
            flexWrap: isMobile ? 'wrap' : 'nowrap',
          }}>
            {[
              { label: 'Field Agents', value: orchStewardsAvail, color: T.gold },
              { label: 'Analysts', value: scoringAgents.length || Object.keys(AGENT_META).length - hiddenScoringIds.size, color: '#60A5FA' },
            ].map(m => (
              <div key={m.label} style={{
                background: `${T.navy}08`, borderRadius: 10,
                padding: '12px 20px', textAlign: 'center', minWidth: 100,
              }}>
                <div style={{ fontSize: 9, fontWeight: 700, color: T.textLight, textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: 4 }}>
                  {m.label}
                </div>
                <div style={{ fontSize: 24, fontWeight: 800, color: m.color }}>
                  {m.value.toLocaleString()}
                </div>
              </div>
            ))}
          </div>
        </div>

      </div>

      {/* NDC Intelligence Agent Cards */}
      <div>
        <h3 style={{ fontSize: 11, fontWeight: 700, color: T.textLight, textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: 16 }}>
          Field Agents &mdash; Drug Data Collection Agents
        </h3>
        <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : isTablet ? 'repeat(2, 1fr)' : 'repeat(3, 1fr)', gap: 12, gridAutoRows: '1fr' }}>
          {Object.entries(STEWARD_META).filter(([key]) => !hiddenStewardIds.has(key) && !manufacturerStewardIds.has(key)).map(([key, meta]) => {
            const swarmAgent = stewardAgents.find(a => a.agent_id === key);
            const health = healthSummary?.agents?.[key];
            const agentStatus = swarmAgent
              ? (swarmAgent.enabled ? (swarmAgent.last_run_status || 'idle') : 'disabled')
              : 'idle';
            const errorRate = health?.error_rate ?? (
              swarmAgent && swarmAgent.products_processed_last_run > 0
                ? Math.round((swarmAgent.error_count_last_run / swarmAgent.products_processed_last_run) * 1000) / 10
                : 0
            );
            const totalProcessed = health?.products_processed_total ?? swarmAgent?.total_products_processed ?? 0;
            const stewardProgressPct = totalProcessed > 0 ? Math.min(Math.round((totalProcessed / TOTAL_PRODUCTS) * 100), 100) : 0;
            const completeness = stewardCompleteness[key];
            const completenessPct = completeness?.completeness_pct ?? null;

            return (
              <div
                key={key}
                onClick={() => navigate(`/agent-detail/${key}`)}
                style={{
                  background: T.white, border: `1px solid ${T.border}`,
                  borderRadius: T.radius, padding: 14, boxShadow: T.shadow,
                  cursor: 'pointer', transition: 'transform 0.15s, box-shadow 0.15s',
                  display: 'flex', flexDirection: 'column' as const,
                }}
                onMouseEnter={e => {
                  (e.currentTarget as HTMLDivElement).style.transform = 'translateY(-2px)';
                  (e.currentTarget as HTMLDivElement).style.boxShadow = T.shadowMd;
                }}
                onMouseLeave={e => {
                  (e.currentTarget as HTMLDivElement).style.transform = 'translateY(0)';
                  (e.currentTarget as HTMLDivElement).style.boxShadow = T.shadow;
                }}
              >
                {/* Header */}
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 10, gap: 6 }}>
                  <div style={{ minWidth: 0, overflow: 'hidden' }}>
                    <div style={{ fontSize: 12, fontWeight: 700, color: T.navyLight, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                      {meta.display}
                    </div>
                    <div style={{ fontSize: 10, color: T.textLight, marginTop: 1, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: '100%' }}>{meta.type}</div>
                  </div>
                  <span style={{
                    fontSize: 8, fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.04em',
                    padding: '2px 7px', borderRadius: 20, flexShrink: 0,
                    ...getStatusBadgeStyle(agentStatus, T),
                  }}>
                    {agentStatus.replace(/_/g, ' ')}
                  </span>
                </div>

                {/* Compact metrics */}
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '6px 10px' }}>
                  <div>
                    <div style={{ fontSize: 8, fontWeight: 600, color: T.textLight, textTransform: 'uppercase', letterSpacing: '0.06em' }}>Last Run</div>
                    <div style={{ fontSize: 12, fontWeight: 700, color: T.textDark, marginTop: 1 }}>{formatTimeAgo(swarmAgent?.last_run_at ?? null)}</div>
                  </div>
                  <div>
                    <div style={{ fontSize: 8, fontWeight: 600, color: T.textLight, textTransform: 'uppercase', letterSpacing: '0.06em' }}>Error Rate</div>
                    <div style={{ fontSize: 12, fontWeight: 700, marginTop: 1, color: errorRate > 10 ? T.danger : errorRate > 5 ? T.warning : T.success }}>{errorRate}%</div>
                  </div>
                  <div>
                    <div style={{ fontSize: 8, fontWeight: 600, color: T.textLight, textTransform: 'uppercase', letterSpacing: '0.06em' }}>Processed</div>
                    <div style={{ fontSize: 12, fontWeight: 700, color: T.textDark, marginTop: 1 }}>{totalProcessed.toLocaleString()}</div>
                  </div>
                  <div>
                    <div style={{ fontSize: 8, fontWeight: 600, color: T.textLight, textTransform: 'uppercase', letterSpacing: '0.06em' }}>Data Completeness</div>
                    <div style={{ fontSize: 12, fontWeight: 700, marginTop: 1, color: completenessPct == null ? T.textLight : completenessPct >= 75 ? '#22C55E' : completenessPct >= 50 ? '#F59E0B' : '#EF4444' }}>
                      {completenessPct != null ? `${completenessPct}%` : '--'}
                    </div>
                  </div>
                </div>

                {/* Progress bar */}
                <div style={{ marginTop: 8 }}>
                  <div style={{ height: 3, borderRadius: 2, background: T.borderLight, overflow: 'hidden' }}>
                    <div style={{
                      width: `${stewardProgressPct}%`, height: '100%', borderRadius: 2,
                      background: agentStatus === 'degraded' || agentStatus === 'rate_limited' ? T.warning : T.success,
                      transition: 'width 0.6s',
                    }} />
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 3, fontSize: 8, color: T.textLight }}>
                    <span>{stewardProgressPct}% complete</span>
                    <span>{totalProcessed.toLocaleString()} / {TOTAL_PRODUCTS.toLocaleString()}</span>
                  </div>
                </div>

                {/* Normalizer controls — target select + Normalize button */}
                {key === 'steward_country_normalizer' && (
                  <div style={{ marginTop: 10 }}>
                    <div style={{ display: 'flex', gap: 6 }}>
                      {!rescoreRunning['steward_country_normalizer'] ? (
                        <>
                          <select
                            value={normalizerTarget}
                            onClick={e => e.stopPropagation()}
                            onChange={e => setNormalizerTarget(e.target.value as NormalizerTarget)}
                            style={{
                              padding: '6px 8px', border: `1px solid ${T.borderLight}`, borderRadius: T.radiusSm,
                              background: '#fff', color: T.textDark, fontSize: 11, fontWeight: 600, cursor: 'pointer',
                            }}
                          >
                            <option value="all">All</option>
                            <option value="products">Products only</option>
                            <option value="manufacturers">Manufacturers only</option>
                            <option value="api_source">API source only</option>
                          </select>
                          <button
                            onClick={async (e) => {
                              e.stopPropagation();
                              const aid = 'steward_country_normalizer';
                              setRescoreRunning(prev => ({ ...prev, [aid]: true }));
                              setRescoreProgress(prev => ({ ...prev, [aid]: null }));
                              // For 'all' we run manufacturers first then products. The
                              // pending flag tells the polling effect to fire products
                              // when the manufacturers run completes.
                              setNormalizerPendingProducts(normalizerTarget === 'all');
                              const firstEndpoint =
                                normalizerTarget === 'products'
                                  ? 'normalize-products'
                                  : normalizerTarget === 'api_source'
                                    ? 'normalize-api-source'
                                    : 'normalize-manufacturers';
                              try {
                                const resp = await fetch(
                                  `${API_BASE}/api/metasupply/swarm/agents/${aid}/${firstEndpoint}`,
                                  {
                                    method: 'POST',
                                    credentials: 'include',
                                    headers: { 'Content-Type': 'application/json' },
                                    body: JSON.stringify({}),
                                  },
                                );
                                if (!resp.ok) {
                                  const errData = await resp.json().catch(() => ({ detail: resp.statusText }));
                                  alert(`Failed to start: ${errData.detail || resp.statusText}`);
                                  setRescoreRunning(prev => ({ ...prev, [aid]: false }));
                                  setNormalizerPendingProducts(false);
                                  return;
                                }
                                const data = await resp.json();
                                setRescoreRunId(prev => ({ ...prev, [aid]: data.run_id }));
                                setRescoreProgress(prev => ({ ...prev, [aid]: {
                                  status: 'running', total_products: 0,
                                  products_completed: 0, products_failed: 0, progress_pct: 0,
                                }}));
                              } catch (err) {
                                alert(`Error: ${err}`);
                                setRescoreRunning(prev => ({ ...prev, [aid]: false }));
                                setNormalizerPendingProducts(false);
                              }
                            }}
                            style={{
                              flex: 1, padding: '6px 0', border: 'none', borderRadius: T.radiusSm,
                              background: T.navyLight, color: '#fff',
                              fontSize: 11, fontWeight: 700, cursor: 'pointer',
                              transition: 'opacity 0.2s',
                            }}
                            onMouseEnter={e => { (e.currentTarget as HTMLButtonElement).style.opacity = '0.85'; }}
                            onMouseLeave={e => { (e.currentTarget as HTMLButtonElement).style.opacity = '1'; }}
                          >
                            Normalize
                          </button>
                        </>
                      ) : (
                        <>
                          <button
                            onClick={async (e) => {
                              e.stopPropagation();
                              const runId = rescoreRunId['steward_country_normalizer'];
                              if (!runId) return;
                              try {
                                const resp = await fetch(`${API_BASE}/api/metasupply/swarm/trigger-all/${runId}/stop`, {
                                  method: 'POST', credentials: 'include',
                                });
                                if (resp.ok) {
                                  setRescoreRunning(prev => ({ ...prev, steward_country_normalizer: false }));
                                  const curProgress = rescoreProgress['steward_country_normalizer'];
                                  if (curProgress) {
                                    setRescoreProgress(prev => ({ ...prev, steward_country_normalizer: { ...curProgress, status: 'stopped' } }));
                                  }
                                } else {
                                  const errData = await resp.json().catch(() => ({ detail: resp.statusText }));
                                  alert(`Failed to stop: ${errData.detail || resp.statusText}`);
                                }
                              } catch (err) {
                                alert(`Error: ${err}`);
                              }
                            }}
                            style={{
                              flex: 1, padding: '6px 0', border: 'none', borderRadius: T.radiusSm,
                              background: '#F59E0B', color: '#fff',
                              fontSize: 11, fontWeight: 700, cursor: 'pointer',
                              transition: 'opacity 0.2s',
                            }}
                            onMouseEnter={e => { (e.currentTarget as HTMLButtonElement).style.opacity = '0.85'; }}
                            onMouseLeave={e => { (e.currentTarget as HTMLButtonElement).style.opacity = '1'; }}
                          >
                            Stop
                          </button>
                          <button
                            onClick={async (e) => {
                              e.stopPropagation();
                              const runId = rescoreRunId['steward_country_normalizer'];
                              if (!runId) return;
                              if (!window.confirm('Kill this normalizer run?')) return;
                              try {
                                const resp = await fetch(`${API_BASE}/api/metasupply/swarm/trigger-all/${runId}`, {
                                  method: 'DELETE', credentials: 'include',
                                });
                                if (resp.ok) {
                                  setRescoreRunning(prev => ({ ...prev, steward_country_normalizer: false }));
                                  setRescoreRunId(prev => ({ ...prev, steward_country_normalizer: null }));
                                  setRescoreProgress(prev => ({ ...prev, steward_country_normalizer: null }));
                                } else {
                                  const errData = await resp.json().catch(() => ({ detail: resp.statusText }));
                                  alert(`Failed to kill: ${errData.detail || resp.statusText}`);
                                }
                              } catch (err) {
                                alert(`Error: ${err}`);
                              }
                            }}
                            style={{
                              flex: 1, padding: '6px 0', border: 'none', borderRadius: T.radiusSm,
                              background: '#EF4444', color: '#fff',
                              fontSize: 11, fontWeight: 700, cursor: 'pointer',
                              transition: 'opacity 0.2s',
                            }}
                            onMouseEnter={e => { (e.currentTarget as HTMLButtonElement).style.opacity = '0.85'; }}
                            onMouseLeave={e => { (e.currentTarget as HTMLButtonElement).style.opacity = '1'; }}
                          >
                            Kill
                          </button>
                        </>
                      )}
                    </div>
                    {rescoreProgress['steward_country_normalizer'] && (
                      <div style={{ marginTop: 6 }}>
                        <div style={{ height: 3, borderRadius: 2, background: T.borderLight, overflow: 'hidden' }}>
                          <div style={{
                            width: `${rescoreProgress['steward_country_normalizer']!.progress_pct}%`, height: '100%', borderRadius: 2,
                            background: rescoreProgress['steward_country_normalizer']!.status === 'completed' ? T.success
                              : rescoreProgress['steward_country_normalizer']!.status === 'stopped' ? '#F59E0B'
                              : T.navyLight,
                            transition: 'width 0.6s',
                          }} />
                        </div>
                        <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 3, fontSize: 8, color: T.textLight }}>
                          <span>
                            {rescoreProgress['steward_country_normalizer']!.status === 'completed' ? 'Complete' :
                             rescoreProgress['steward_country_normalizer']!.status === 'stopped' ? 'Stopped' :
                             `${rescoreProgress['steward_country_normalizer']!.progress_pct}%`}
                          </span>
                          <span>
                            {rescoreProgress['steward_country_normalizer']!.products_completed.toLocaleString()} / {rescoreProgress['steward_country_normalizer']!.total_products.toLocaleString()}
                            {rescoreProgress['steward_country_normalizer']!.products_failed > 0 && ` (${rescoreProgress['steward_country_normalizer']!.products_failed.toLocaleString()} failed)`}
                          </span>
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* Manufacturer Intelligence Agent Cards */}
      <div>
        <h3 style={{ fontSize: 11, fontWeight: 700, color: T.textLight, textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: 16 }}>
          Manufacturer Field Agents &mdash; Data Collection Agents
        </h3>
        <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : isTablet ? 'repeat(2, 1fr)' : 'repeat(3, 1fr)', gap: 12, gridAutoRows: '1fr' }}>
          {Object.entries(STEWARD_META).filter(([key]) => manufacturerStewardIds.has(key)).map(([key, meta]) => {
            const swarmAgent = stewardAgents.find(a => a.agent_id === key);
            const health = healthSummary?.agents?.[key];
            const agentStatus = swarmAgent
              ? (swarmAgent.enabled ? (swarmAgent.last_run_status || 'idle') : 'disabled')
              : 'idle';
            const errorRate = health?.error_rate ?? (
              swarmAgent && swarmAgent.products_processed_last_run > 0
                ? Math.round((swarmAgent.error_count_last_run / swarmAgent.products_processed_last_run) * 1000) / 10
                : 0
            );
            const totalProcessed = health?.products_processed_total ?? swarmAgent?.total_products_processed ?? 0;
            const stewardProgressPct = totalProcessed > 0 ? Math.min(Math.round((totalProcessed / TOTAL_PRODUCTS) * 100), 100) : 0;
            const completeness = stewardCompleteness[key];
            const completenessPct = completeness?.completeness_pct ?? null;

            return (
              <div
                key={key}
                onClick={() => navigate(`/agent-detail/${key}`)}
                style={{
                  background: T.white, border: `1px solid ${T.border}`,
                  borderRadius: T.radius, padding: 14, boxShadow: T.shadow,
                  cursor: 'pointer', transition: 'transform 0.15s, box-shadow 0.15s',
                  display: 'flex', flexDirection: 'column' as const,
                }}
                onMouseEnter={e => {
                  (e.currentTarget as HTMLDivElement).style.transform = 'translateY(-2px)';
                  (e.currentTarget as HTMLDivElement).style.boxShadow = T.shadowMd;
                }}
                onMouseLeave={e => {
                  (e.currentTarget as HTMLDivElement).style.transform = 'translateY(0)';
                  (e.currentTarget as HTMLDivElement).style.boxShadow = T.shadow;
                }}
              >
                {/* Header */}
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 10, gap: 6 }}>
                  <div style={{ minWidth: 0, overflow: 'hidden' }}>
                    <div style={{ fontSize: 12, fontWeight: 700, color: T.navyLight, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                      {meta.display}
                    </div>
                    <div style={{ fontSize: 10, color: T.textLight, marginTop: 1, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: '100%' }}>{meta.type}</div>
                  </div>
                  <span style={{
                    fontSize: 8, fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.04em',
                    padding: '2px 7px', borderRadius: 20, flexShrink: 0,
                    ...getStatusBadgeStyle(agentStatus, T),
                  }}>
                    {agentStatus.replace(/_/g, ' ')}
                  </span>
                </div>

                {/* Compact metrics */}
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '6px 10px' }}>
                  <div>
                    <div style={{ fontSize: 8, fontWeight: 600, color: T.textLight, textTransform: 'uppercase', letterSpacing: '0.06em' }}>Last Run</div>
                    <div style={{ fontSize: 12, fontWeight: 700, color: T.textDark, marginTop: 1 }}>{formatTimeAgo(swarmAgent?.last_run_at ?? null)}</div>
                  </div>
                  <div>
                    <div style={{ fontSize: 8, fontWeight: 600, color: T.textLight, textTransform: 'uppercase', letterSpacing: '0.06em' }}>Error Rate</div>
                    <div style={{ fontSize: 12, fontWeight: 700, marginTop: 1, color: errorRate > 10 ? T.danger : errorRate > 5 ? T.warning : T.success }}>{errorRate}%</div>
                  </div>
                  <div>
                    <div style={{ fontSize: 8, fontWeight: 600, color: T.textLight, textTransform: 'uppercase', letterSpacing: '0.06em' }}>Processed</div>
                    <div style={{ fontSize: 12, fontWeight: 700, color: T.textDark, marginTop: 1 }}>{totalProcessed.toLocaleString()}</div>
                  </div>
                  <div>
                    <div style={{ fontSize: 8, fontWeight: 600, color: T.textLight, textTransform: 'uppercase', letterSpacing: '0.06em' }}>Data Completeness</div>
                    <div style={{ fontSize: 12, fontWeight: 700, marginTop: 1, color: completenessPct == null ? T.textLight : completenessPct >= 75 ? '#22C55E' : completenessPct >= 50 ? '#F59E0B' : '#EF4444' }}>
                      {completenessPct != null ? `${completenessPct}%` : '--'}
                    </div>
                  </div>
                </div>

                {/* Progress bar */}
                <div style={{ marginTop: 8 }}>
                  <div style={{ height: 3, borderRadius: 2, background: T.borderLight, overflow: 'hidden' }}>
                    <div style={{
                      width: `${stewardProgressPct}%`, height: '100%', borderRadius: 2,
                      background: agentStatus === 'degraded' || agentStatus === 'rate_limited' ? T.warning : T.success,
                      transition: 'width 0.6s',
                    }} />
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 3, fontSize: 8, color: T.textLight }}>
                    <span>{stewardProgressPct}% complete</span>
                    <span>{totalProcessed.toLocaleString()} / {TOTAL_PRODUCTS.toLocaleString()}</span>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Agent Health Cards */}
      <div>
        <h3 style={{ fontSize: 11, fontWeight: 700, color: T.textLight, textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: 16 }}>
          Agent Health &mdash; Analyst Agents
        </h3>
        <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : isTablet ? 'repeat(2, 1fr)' : 'repeat(3, 1fr)', gap: 12, gridAutoRows: '1fr' }}>
          {scoringAgents.length > 0 ? scoringAgents.map(agent => {
            const health = healthSummary?.agents?.[agent.agent_id];
            const agentStatus = agent.enabled ? (agent.last_run_status || 'idle') : 'disabled';
            const meta = AGENT_META[agent.agent_id] || { display: agent.name || agent.agent_id.replace(/_/g, ' '), type: 'Agent' };
            const errorRate = health?.error_rate ?? (
              agent.products_processed_last_run > 0
                ? Math.round((agent.error_count_last_run / agent.products_processed_last_run) * 1000) / 10
                : 0
            );
            const totalProcessed = health?.products_processed_total ?? agent.total_products_processed ?? agent.products_processed_last_run;
            const agentProgressPct = totalProcessed > 0 ? Math.min(Math.round((totalProcessed / TOTAL_PRODUCTS) * 100), 100) : 0;

            return (
              <div
                key={agent.agent_id}
                style={{
                  background: T.white, border: `1px solid ${T.border}`,
                  borderRadius: T.radius, padding: 14, boxShadow: T.shadow,
                  opacity: agent.enabled ? 1 : 0.6,
                  transition: 'transform 0.15s, box-shadow 0.15s',
                  display: 'flex', flexDirection: 'column' as const,
                }}
                onMouseEnter={e => {
                  (e.currentTarget as HTMLDivElement).style.transform = 'translateY(-2px)';
                  (e.currentTarget as HTMLDivElement).style.boxShadow = T.shadowMd;
                }}
                onMouseLeave={e => {
                  (e.currentTarget as HTMLDivElement).style.transform = 'translateY(0)';
                  (e.currentTarget as HTMLDivElement).style.boxShadow = T.shadow;
                }}
              >
                {/* Header */}
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 14, gap: 6 }}>
                  <div style={{ minWidth: 0, overflow: 'hidden' }}>
                    <div
                      onClick={() => navigate(`/agent-detail/${agent.agent_id}`)}
                      style={{ fontSize: 14, fontWeight: 700, color: T.navyLight, cursor: 'pointer', textDecoration: 'none', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}
                      onMouseEnter={e => { (e.currentTarget as HTMLDivElement).style.textDecoration = 'underline'; }}
                      onMouseLeave={e => { (e.currentTarget as HTMLDivElement).style.textDecoration = 'none'; }}
                    >{meta.display}</div>
                    <div style={{ fontSize: 11, color: T.textLight, marginTop: 2, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{meta.type}</div>
                  </div>
                  <span style={{
                    fontSize: 9, fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.04em',
                    padding: '3px 10px', borderRadius: 20, flexShrink: 0,
                    ...getStatusBadgeStyle(agentStatus, T),
                  }}>
                    {agentStatus.replace(/_/g, ' ')}
                  </span>
                </div>

                {/* Metrics grid */}
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px 16px' }}>
                  <div>
                    <div style={{ fontSize: 9, fontWeight: 600, color: T.textLight, textTransform: 'uppercase', letterSpacing: '0.06em' }}>Last Run</div>
                    <div style={{ fontSize: 15, fontWeight: 700, color: T.textDark, marginTop: 2 }}>{formatTimeAgo(agent.last_run_at)}</div>
                  </div>
                  <div>
                    <div style={{ fontSize: 9, fontWeight: 600, color: T.textLight, textTransform: 'uppercase', letterSpacing: '0.06em' }}>Error Rate</div>
                    <div style={{ fontSize: 15, fontWeight: 700, marginTop: 2, color: errorRate > 10 ? T.danger : errorRate > 5 ? T.warning : T.success }}>{errorRate}%</div>
                  </div>
                  <div>
                    <div style={{ fontSize: 9, fontWeight: 600, color: T.textLight, textTransform: 'uppercase', letterSpacing: '0.06em' }}>Products Scored</div>
                    <div style={{ fontSize: 15, fontWeight: 700, color: T.textDark, marginTop: 2 }}>{totalProcessed.toLocaleString()}</div>
                  </div>
                  <div>
                    <div style={{ fontSize: 9, fontWeight: 600, color: T.textLight, textTransform: 'uppercase', letterSpacing: '0.06em' }}>Next Run</div>
                    <div style={{ fontSize: 15, fontWeight: 700, color: T.textDark, marginTop: 2 }}>{agent.next_scheduled_run ? formatTimeAgo(agent.next_scheduled_run) : 'N/A'}</div>
                  </div>
                </div>

                {/* Progress bar */}
                <div style={{ marginTop: 14 }}>
                  <div style={{ height: 4, borderRadius: 2, background: T.borderLight, overflow: 'hidden' }}>
                    <div style={{
                      width: `${agentProgressPct}%`, height: '100%', borderRadius: 2,
                      background: agentStatus === 'degraded' || agentStatus === 'rate_limited' ? T.warning : T.success,
                      transition: 'width 0.6s',
                    }} />
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 4, fontSize: 9, color: T.textLight }}>
                    <span>{agentProgressPct}% complete</span>
                    <span>{totalProcessed.toLocaleString()} / {TOTAL_PRODUCTS.toLocaleString()}</span>
                  </div>
                </div>

                {/* Circuit breaker */}
                {health?.circuit_breaker_state && health.circuit_breaker_state !== 'closed' && (
                  <div style={{
                    marginTop: 10, padding: '5px 10px', borderRadius: T.radiusSm,
                    background: health.circuit_breaker_state === 'open' ? T.dangerBg : T.warningBg,
                    fontSize: 10, fontWeight: 600,
                    color: health.circuit_breaker_state === 'open' ? T.danger : T.warning,
                  }}>
                    Circuit Breaker: {health.circuit_breaker_state.toUpperCase()}
                    {health.consecutive_failures > 0 && ` (${health.consecutive_failures} failures)`}
                  </div>
                )}

                {/* Rescore All Products button — all scoring agents */}
                {(agent.agent_id === 'coo_scoring' || agent.agent_id === 'qrs' || agent.agent_id === 'pns' || agent.agent_id === 'steward_country_normalizer') && (
                  <div style={{ marginTop: 12 }}>
                    {/* Start / Stop / Kill row */}
                    <div style={{ display: 'flex', gap: 6 }}>
                      {!rescoreRunning[agent.agent_id] ? (
                        <button
                          onClick={async (e) => {
                            e.stopPropagation();
                            setRescoreRunning(prev => ({ ...prev, [agent.agent_id]: true }));
                            setRescoreProgress(prev => ({ ...prev, [agent.agent_id]: null }));
                            try {
                              const resp = await fetch(`${API_BASE}/api/metasupply/swarm/trigger-all`, {
                                method: 'POST',
                                credentials: 'include',
                                headers: { 'Content-Type': 'application/json' },
                                body: JSON.stringify({
                                  agent_id: agent.agent_id,
                                  ...((agent.agent_id === 'coo_scoring' || agent.agent_id === 'steward_country_normalizer' || agent.agent_id === 'qrs') ? { score_only: true } : {}),
                                }),
                              });
                              if (!resp.ok) {
                                const errData = await resp.json().catch(() => ({ detail: resp.statusText }));
                                alert(`Failed to start rescore: ${errData.detail || resp.statusText}`);
                                setRescoreRunning(prev => ({ ...prev, [agent.agent_id]: false }));
                                return;
                              }
                              const data = await resp.json();
                              setRescoreRunId(prev => ({ ...prev, [agent.agent_id]: data.run_id }));
                              setRescoreProgress(prev => ({ ...prev, [agent.agent_id]: {
                                status: 'running', total_products: data.total_products,
                                products_completed: 0, products_failed: 0, progress_pct: 0,
                              }}));
                            } catch (err) {
                              alert(`Error: ${err}`);
                              setRescoreRunning(prev => ({ ...prev, [agent.agent_id]: false }));
                            }
                          }}
                          style={{
                            flex: 1, padding: '8px 0', border: 'none', borderRadius: T.radiusSm,
                            background: T.navyLight, color: '#fff',
                            fontSize: 12, fontWeight: 700, cursor: 'pointer',
                            transition: 'opacity 0.2s',
                          }}
                          onMouseEnter={e => { (e.currentTarget as HTMLButtonElement).style.opacity = '0.85'; }}
                          onMouseLeave={e => { (e.currentTarget as HTMLButtonElement).style.opacity = '1'; }}
                        >
                          {agent.agent_id === 'steward_country_normalizer' ? 'Normalize All Products' : 'Rescore All Products'}
                        </button>
                      ) : (
                        <>
                          {/* Stop button */}
                          <button
                            onClick={async (e) => {
                              e.stopPropagation();
                              const runId = rescoreRunId[agent.agent_id];
                              if (!runId) return;
                              try {
                                const resp = await fetch(`${API_BASE}/api/metasupply/swarm/trigger-all/${runId}/stop`, {
                                  method: 'POST', credentials: 'include',
                                });
                                if (resp.ok) {
                                  setRescoreRunning(prev => ({ ...prev, [agent.agent_id]: false }));
                                  const curProgress = rescoreProgress[agent.agent_id];
                                  if (curProgress) {
                                    setRescoreProgress(prev => ({ ...prev, [agent.agent_id]: { ...curProgress, status: 'stopped' } }));
                                  }
                                } else {
                                  const errData = await resp.json().catch(() => ({ detail: resp.statusText }));
                                  alert(`Failed to stop: ${errData.detail || resp.statusText}`);
                                }
                              } catch (err) {
                                alert(`Error: ${err}`);
                              }
                            }}
                            style={{
                              flex: 1, padding: '8px 0', border: 'none', borderRadius: T.radiusSm,
                              background: '#F59E0B', color: '#fff',
                              fontSize: 12, fontWeight: 700, cursor: 'pointer',
                              transition: 'opacity 0.2s',
                            }}
                            onMouseEnter={e => { (e.currentTarget as HTMLButtonElement).style.opacity = '0.85'; }}
                            onMouseLeave={e => { (e.currentTarget as HTMLButtonElement).style.opacity = '1'; }}
                          >
                            Stop
                          </button>
                          {/* Kill button */}
                          <button
                            onClick={async (e) => {
                              e.stopPropagation();
                              const runId = rescoreRunId[agent.agent_id];
                              if (!runId) return;
                              if (!window.confirm('Kill this rescore run? The run will be removed from tracking.')) return;
                              try {
                                const resp = await fetch(`${API_BASE}/api/metasupply/swarm/trigger-all/${runId}`, {
                                  method: 'DELETE', credentials: 'include',
                                });
                                if (resp.ok) {
                                  setRescoreRunning(prev => ({ ...prev, [agent.agent_id]: false }));
                                  setRescoreRunId(prev => ({ ...prev, [agent.agent_id]: null }));
                                  setRescoreProgress(prev => ({ ...prev, [agent.agent_id]: null }));
                                } else {
                                  const errData = await resp.json().catch(() => ({ detail: resp.statusText }));
                                  alert(`Failed to kill: ${errData.detail || resp.statusText}`);
                                }
                              } catch (err) {
                                alert(`Error: ${err}`);
                              }
                            }}
                            style={{
                              flex: 1, padding: '8px 0', border: 'none', borderRadius: T.radiusSm,
                              background: '#EF4444', color: '#fff',
                              fontSize: 12, fontWeight: 700, cursor: 'pointer',
                              transition: 'opacity 0.2s',
                            }}
                            onMouseEnter={e => { (e.currentTarget as HTMLButtonElement).style.opacity = '0.85'; }}
                            onMouseLeave={e => { (e.currentTarget as HTMLButtonElement).style.opacity = '1'; }}
                          >
                            Kill
                          </button>
                        </>
                      )}
                    </div>

                    {/* Progress bar */}
                    {rescoreProgress[agent.agent_id] && (
                      <div style={{ marginTop: 8 }}>
                        <div style={{ height: 4, borderRadius: 2, background: T.borderLight, overflow: 'hidden' }}>
                          <div style={{
                            width: `${rescoreProgress[agent.agent_id]!.progress_pct}%`, height: '100%', borderRadius: 2,
                            background: rescoreProgress[agent.agent_id]!.status === 'completed' ? T.success
                              : rescoreProgress[agent.agent_id]!.status === 'stopped' ? '#F59E0B'
                              : T.navyLight,
                            transition: 'width 0.6s',
                          }} />
                        </div>
                        <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 4, fontSize: 9, color: T.textLight }}>
                          <span>
                            {rescoreProgress[agent.agent_id]!.status === 'completed' ? 'Complete' :
                             rescoreProgress[agent.agent_id]!.status === 'stopped' ? 'Stopped' :
                             `${rescoreProgress[agent.agent_id]!.progress_pct}%`}
                          </span>
                          <span>
                            {rescoreProgress[agent.agent_id]!.products_completed.toLocaleString()} / {rescoreProgress[agent.agent_id]!.total_products.toLocaleString()}
                            {rescoreProgress[agent.agent_id]!.products_failed > 0 && ` (${rescoreProgress[agent.agent_id]!.products_failed.toLocaleString()} failed)`}
                          </span>
                        </div>
                      </div>
                    )}

                    {/* Reset Scores button — hide for normalizer (no scores to reset) */}
                    {agent.agent_id !== 'steward_country_normalizer' && <button
                      onClick={async (e) => {
                        e.stopPropagation();
                        if (!window.confirm('Reset all scores for this agent? This will remove all scored products, Data Inspector findings, and clear rescore values from the products table. This cannot be undone.')) return;
                        try {
                          const resp = await fetch(`${API_BASE}/api/metasupply/swarm/trigger-all/reset-scores?agent_id=${agent.agent_id}`, {
                            method: 'POST', credentials: 'include',
                          });
                          if (resp.ok) {
                            const data = await resp.json();
                            alert(`Reset complete: ${data.scored_products_deleted} scored products deleted, ${data.findings_deleted} findings deleted, ${data.products_cleared} product rescore values cleared.`);
                            fetchData();
                          } else {
                            const errData = await resp.json().catch(() => ({ detail: resp.statusText }));
                            alert(`Failed to reset: ${errData.detail || resp.statusText}`);
                          }
                        } catch (err) {
                          alert(`Error: ${err}`);
                        }
                      }}
                      style={{
                        width: '100%', marginTop: 6, padding: '6px 0', border: `1px solid ${T.border}`,
                        borderRadius: T.radiusSm, background: T.white, color: T.textLight,
                        fontSize: 11, fontWeight: 600, cursor: 'pointer',
                        transition: 'all 0.2s',
                      }}
                      onMouseEnter={e => {
                        (e.currentTarget as HTMLButtonElement).style.borderColor = '#EF4444';
                        (e.currentTarget as HTMLButtonElement).style.color = '#EF4444';
                      }}
                      onMouseLeave={e => {
                        (e.currentTarget as HTMLButtonElement).style.borderColor = T.border;
                        (e.currentTarget as HTMLButtonElement).style.color = T.textLight;
                      }}
                    >
                      Reset / Remove All Scores
                    </button>}

                    {/* LLM Analysis Queue Section — hide for normalizer */}
                    {agent.agent_id !== 'steward_country_normalizer' &&
                    <div style={{
                      marginTop: 14, paddingTop: 12,
                      borderTop: `1px solid ${T.border}`,
                    }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                        <div style={{ fontSize: 11, fontWeight: 700, color: T.textDark }}>
                          LLM Analysis Queue
                        </div>
                        <div style={{ fontSize: 10, color: T.textLight }}>
                          {(llmQueuePending[agent.agent_id] || 0).toLocaleString()} pending · {(llmQueueCompleted[agent.agent_id] || 0).toLocaleString()} done
                        </div>
                      </div>

                      {/* Run / Stop / Kill row */}
                      <div style={{ display: 'flex', gap: 6 }}>
                        {!llmQueueRunning[agent.agent_id] ? (
                          <button
                            disabled={(llmQueuePending[agent.agent_id] || 0) === 0}
                            onClick={async (e) => {
                              e.stopPropagation();
                              if ((llmQueuePending[agent.agent_id] || 0) === 0) return;
                              try {
                                const resp = await fetch(`${API_BASE}/api/metasupply/swarm/llm-queue/run?agent_id=${agent.agent_id}&batch_size=10`, {
                                  method: 'POST', credentials: 'include',
                                });
                                if (!resp.ok) {
                                  const errData = await resp.json().catch(() => ({ detail: resp.statusText }));
                                  alert(`Failed to start LLM queue: ${errData.detail || resp.statusText}`);
                                  return;
                                }
                                const data = await resp.json();
                                if (data.status === 'empty') {
                                  alert('No products pending LLM analysis');
                                  return;
                                }
                                setLlmQueueRunId(prev => ({ ...prev, [agent.agent_id]: data.run_id }));
                                setLlmQueueRunning(prev => ({ ...prev, [agent.agent_id]: true }));
                                setLlmQueueProgress(prev => ({ ...prev, [agent.agent_id]: {
                                  status: 'running', total: data.total,
                                  processed: 0, failed: 0, progress_pct: 0,
                                }}));
                              } catch (err) {
                                alert(`Error: ${err}`);
                              }
                            }}
                            style={{
                              flex: 1, padding: '7px 0', border: 'none', borderRadius: T.radiusSm,
                              background: (llmQueuePending[agent.agent_id] || 0) === 0 ? T.borderLight : '#7C3AED',
                              color: (llmQueuePending[agent.agent_id] || 0) === 0 ? T.textLight : '#fff',
                              fontSize: 11, fontWeight: 700,
                              cursor: (llmQueuePending[agent.agent_id] || 0) === 0 ? 'not-allowed' : 'pointer',
                              transition: 'opacity 0.2s',
                            }}
                            onMouseEnter={e => { if ((llmQueuePending[agent.agent_id] || 0) > 0) (e.currentTarget as HTMLButtonElement).style.opacity = '0.85'; }}
                            onMouseLeave={e => { (e.currentTarget as HTMLButtonElement).style.opacity = '1'; }}
                          >
                            Run LLM Queue ({(llmQueuePending[agent.agent_id] || 0).toLocaleString()})
                          </button>
                        ) : (
                          <>
                            {/* Stop LLM button */}
                            <button
                              onClick={async (e) => {
                                e.stopPropagation();
                                const runId = llmQueueRunId[agent.agent_id];
                                if (!runId) return;
                                try {
                                  const resp = await fetch(`${API_BASE}/api/metasupply/swarm/llm-queue/${runId}/stop`, {
                                    method: 'POST', credentials: 'include',
                                  });
                                  if (resp.ok) {
                                    setLlmQueueRunning(prev => ({ ...prev, [agent.agent_id]: false }));
                                    const curProgress = llmQueueProgress[agent.agent_id];
                                    if (curProgress) {
                                      setLlmQueueProgress(prev => ({ ...prev, [agent.agent_id]: { ...curProgress, status: 'stopped' } }));
                                    }
                                  } else {
                                    const errData = await resp.json().catch(() => ({ detail: resp.statusText }));
                                    alert(`Failed to stop: ${errData.detail || resp.statusText}`);
                                  }
                                } catch (err) {
                                  alert(`Error: ${err}`);
                                }
                              }}
                              style={{
                                flex: 1, padding: '7px 0', border: 'none', borderRadius: T.radiusSm,
                                background: '#F59E0B', color: '#fff',
                                fontSize: 11, fontWeight: 700, cursor: 'pointer',
                                transition: 'opacity 0.2s',
                              }}
                              onMouseEnter={e => { (e.currentTarget as HTMLButtonElement).style.opacity = '0.85'; }}
                              onMouseLeave={e => { (e.currentTarget as HTMLButtonElement).style.opacity = '1'; }}
                            >
                              Stop
                            </button>
                            {/* Kill LLM button */}
                            <button
                              onClick={async (e) => {
                                e.stopPropagation();
                                const runId = llmQueueRunId[agent.agent_id];
                                if (!runId) return;
                                if (!window.confirm('Kill LLM queue processing? The run will be removed.')) return;
                                try {
                                  const resp = await fetch(`${API_BASE}/api/metasupply/swarm/llm-queue/${runId}`, {
                                    method: 'DELETE', credentials: 'include',
                                  });
                                  if (resp.ok) {
                                    setLlmQueueRunning(prev => ({ ...prev, [agent.agent_id]: false }));
                                    setLlmQueueRunId(prev => ({ ...prev, [agent.agent_id]: null }));
                                    setLlmQueueProgress(prev => ({ ...prev, [agent.agent_id]: null }));
                                    fetchLlmQueueStatus();
                                  } else {
                                    const errData = await resp.json().catch(() => ({ detail: resp.statusText }));
                                    alert(`Failed to kill: ${errData.detail || resp.statusText}`);
                                  }
                                } catch (err) {
                                  alert(`Error: ${err}`);
                                }
                              }}
                              style={{
                                flex: 1, padding: '7px 0', border: 'none', borderRadius: T.radiusSm,
                                background: '#EF4444', color: '#fff',
                                fontSize: 11, fontWeight: 700, cursor: 'pointer',
                                transition: 'opacity 0.2s',
                              }}
                              onMouseEnter={e => { (e.currentTarget as HTMLButtonElement).style.opacity = '0.85'; }}
                              onMouseLeave={e => { (e.currentTarget as HTMLButtonElement).style.opacity = '1'; }}
                            >
                              Kill
                            </button>
                          </>
                        )}
                      </div>

                      {/* LLM Queue Progress bar */}
                      {llmQueueProgress[agent.agent_id] && (
                        <div style={{ marginTop: 6 }}>
                          <div style={{ height: 4, borderRadius: 2, background: T.borderLight, overflow: 'hidden' }}>
                            <div style={{
                              width: `${llmQueueProgress[agent.agent_id]!.progress_pct}%`, height: '100%', borderRadius: 2,
                              background: llmQueueProgress[agent.agent_id]!.status === 'completed' ? T.success
                                : llmQueueProgress[agent.agent_id]!.status === 'stopped' ? '#F59E0B'
                                : '#7C3AED',
                              transition: 'width 0.6s',
                            }} />
                          </div>
                          <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 3, fontSize: 9, color: T.textLight }}>
                            <span>
                              {llmQueueProgress[agent.agent_id]!.status === 'completed' ? 'Complete' :
                               llmQueueProgress[agent.agent_id]!.status === 'stopped' ? 'Stopped' :
                               `${llmQueueProgress[agent.agent_id]!.progress_pct}%`}
                            </span>
                            <span>
                              {llmQueueProgress[agent.agent_id]!.processed.toLocaleString()} / {llmQueueProgress[agent.agent_id]!.total.toLocaleString()}
                              {llmQueueProgress[agent.agent_id]!.failed > 0 && ` (${llmQueueProgress[agent.agent_id]!.failed.toLocaleString()} failed)`}
                            </span>
                          </div>
                        </div>
                      )}

                      {/* Reset LLM Queue button */}
                      <button
                        onClick={async (e) => {
                          e.stopPropagation();
                          if (!window.confirm('Reset LLM queue? This will mark all LLM-analyzed findings as pending so they can be re-analyzed.')) return;
                          try {
                            const resp = await fetch(`${API_BASE}/api/metasupply/swarm/llm-queue/reset?agent_id=${agent.agent_id}`, {
                              method: 'POST', credentials: 'include',
                            });
                            if (resp.ok) {
                              const data = await resp.json();
                              alert(`LLM queue reset: ${data.findings_reset} findings marked as pending.`);
                              fetchLlmQueueStatus();
                            } else {
                              const errData = await resp.json().catch(() => ({ detail: resp.statusText }));
                              alert(`Failed to reset: ${errData.detail || resp.statusText}`);
                            }
                          } catch (err) {
                            alert(`Error: ${err}`);
                          }
                        }}
                        style={{
                          width: '100%', marginTop: 6, padding: '5px 0', border: `1px solid ${T.border}`,
                          borderRadius: T.radiusSm, background: T.white, color: T.textLight,
                          fontSize: 10, fontWeight: 600, cursor: 'pointer',
                          transition: 'all 0.2s',
                        }}
                        onMouseEnter={e => {
                          (e.currentTarget as HTMLButtonElement).style.borderColor = '#7C3AED';
                          (e.currentTarget as HTMLButtonElement).style.color = '#7C3AED';
                        }}
                        onMouseLeave={e => {
                          (e.currentTarget as HTMLButtonElement).style.borderColor = T.border;
                          (e.currentTarget as HTMLButtonElement).style.color = T.textLight;
                        }}
                      >
                        Reset LLM Queue
                      </button>
                    </div>}
                  </div>
                )}
              </div>
            );
          }) : (
            /* Placeholder cards when swarm is offline */
            Object.entries(AGENT_META).map(([key, meta]) => (
              <div key={key} style={{
                background: T.white, border: `1px solid ${T.border}`,
                borderRadius: T.radius, padding: 20, boxShadow: T.shadow, opacity: 0.5,
              }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 14, gap: 6 }}>
                  <div style={{ minWidth: 0, overflow: 'hidden' }}>
                    <div
                      onClick={() => navigate(`/agent-detail/${key}`)}
                      style={{ fontSize: 14, fontWeight: 700, color: T.navyLight, cursor: 'pointer', textDecoration: 'none', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}
                      onMouseEnter={e => { (e.currentTarget as HTMLDivElement).style.textDecoration = 'underline'; }}
                      onMouseLeave={e => { (e.currentTarget as HTMLDivElement).style.textDecoration = 'none'; }}
                    >{meta.display}</div>
                    <div style={{ fontSize: 11, color: T.textLight, marginTop: 2, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{meta.type}</div>
                  </div>
                  <span style={{
                    fontSize: 9, fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.04em',
                    padding: '3px 10px', borderRadius: 20, flexShrink: 0,
                    background: T.borderLight, color: T.textLight, border: `1px solid ${T.border}`,
                  }}>
                    Offline
                  </span>
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px 16px' }}>
                  {['Last Run', 'Error Rate', 'Products Scored', 'Next Run'].map(label => (
                    <div key={label}>
                      <div style={{ fontSize: 9, fontWeight: 600, color: T.textLight, textTransform: 'uppercase', letterSpacing: '0.06em' }}>{label}</div>
                      <div style={{ fontSize: 15, fontWeight: 700, color: T.textLight, marginTop: 2 }}>--</div>
                    </div>
                  ))}
                </div>
                <div style={{ marginTop: 14 }}>
                  <div style={{ height: 4, borderRadius: 2, background: T.borderLight }} />
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 4, fontSize: 9, color: T.textLight }}>
                    <span>Waiting for service</span>
                    <span>0 / {TOTAL_PRODUCTS.toLocaleString()}</span>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      </div>

      {/* Batch Processing Progress */}
      <div style={{ background: T.white, border: `1px solid ${T.border}`, borderRadius: T.radius, padding: 24, boxShadow: T.shadow }}>
        <div style={{
          display: 'flex', flexDirection: isMobile ? 'column' : 'row',
          justifyContent: 'space-between', alignItems: isMobile ? 'flex-start' : 'center',
          gap: 16, marginBottom: 16,
        }}>
          <h3 style={{ fontSize: 11, fontWeight: 700, color: T.textLight, textTransform: 'uppercase', letterSpacing: '0.1em', margin: 0 }}>
            {batchProgress?.active_batch ? `Current Batch \u2014 ${batchProgress.active_batch}` : 'Batch Processing Progress'}
          </h3>
          <div style={{ display: 'flex', gap: 24 }}>
            {[
              { value: totalItems.toLocaleString(), label: 'Total' },
              { value: completedItems.toLocaleString(), label: 'Processed' },
              { value: (totalItems - completedItems).toLocaleString(), label: 'Remaining' },
            ].map(stat => (
              <div key={stat.label} style={{ textAlign: 'center' }}>
                <div style={{ fontSize: 18, fontWeight: 800, color: T.textDark }}>{stat.value}</div>
                <div style={{ fontSize: 9, fontWeight: 600, color: T.textLight, textTransform: 'uppercase', letterSpacing: '0.06em' }}>{stat.label}</div>
              </div>
            ))}
          </div>
        </div>

        {/* Big progress bar */}
        <div style={{ marginBottom: 16 }}>
          <div style={{ height: 10, borderRadius: 5, background: T.borderLight, overflow: 'hidden' }}>
            <div style={{
              width: `${Math.min(progressPct, 100)}%`, height: '100%', borderRadius: 5,
              background: `linear-gradient(90deg, ${T.navyLight}, ${T.success})`,
              transition: 'width 1s ease',
            }} />
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 6, fontSize: 11, color: T.textLight }}>
            <span>{overallStatus !== 'offline' ? 'Processing...' : 'Swarm service offline'}</span>
            <span>{Math.min(progressPct, 100)}% &mdash; {completedItems.toLocaleString()} of {totalItems.toLocaleString()} NDCs</span>
          </div>
        </div>

        {/* Recent Runs Table */}
        {agents.length > 0 && (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr>
                  {['Agent', 'Status', 'Last Run', 'Products', 'Errors', 'Error Rate'].map(col => (
                    <th key={col} style={{
                      fontSize: 9, fontWeight: 700, color: T.textLight, textTransform: 'uppercase',
                      letterSpacing: '0.08em', padding: '8px 12px', textAlign: 'left',
                      borderBottom: `1px solid ${T.border}`,
                    }}>
                      {col}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {agents.filter(a => !hiddenScoringIds.has(a.agent_id)).map(agent => {
                  const health = healthSummary?.agents?.[agent.agent_id];
                  const meta = AGENT_META[agent.agent_id] || STEWARD_META[agent.agent_id] || { display: agent.name || agent.agent_id.replace(/_/g, ' '), type: '' };
                  const agentStatus = agent.enabled ? (agent.last_run_status || 'idle') : 'disabled';
                  const errorRate = health?.error_rate ?? (
                    agent.products_processed_last_run > 0
                      ? Math.round((agent.error_count_last_run / agent.products_processed_last_run) * 1000) / 10
                      : 0
                  );
                  return (
                    <tr key={agent.agent_id} style={{ transition: 'background 0.15s' }}
                      onMouseEnter={e => { (e.currentTarget as HTMLTableRowElement).style.background = T.offWhite; }}
                      onMouseLeave={e => { (e.currentTarget as HTMLTableRowElement).style.background = 'transparent'; }}
                    >
                      <td style={{ fontSize: 12, fontWeight: 600, color: T.textDark, padding: '10px 12px', borderBottom: `1px solid ${T.borderLight}` }}>
                        {meta.display}
                      </td>
                      <td style={{ padding: '10px 12px', borderBottom: `1px solid ${T.borderLight}` }}>
                        <span style={{
                          fontSize: 9, fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.04em',
                          padding: '2px 8px', borderRadius: 12, display: 'inline-block',
                          ...getStatusBadgeStyle(agentStatus, T),
                        }}>
                          {agentStatus.replace(/_/g, ' ')}
                        </span>
                      </td>
                      <td style={{ fontSize: 12, color: T.textMid, padding: '10px 12px', borderBottom: `1px solid ${T.borderLight}` }}>
                        {formatTimeAgo(agent.last_run_at)}
                      </td>
                      <td style={{ fontSize: 12, fontWeight: 700, color: T.textDark, padding: '10px 12px', borderBottom: `1px solid ${T.borderLight}`, fontVariantNumeric: 'tabular-nums' }}>
                        {agent.products_processed_last_run.toLocaleString()}
                      </td>
                      <td style={{
                        fontSize: 12, fontWeight: 600, padding: '10px 12px', borderBottom: `1px solid ${T.borderLight}`,
                        color: agent.error_count_last_run > 10 ? T.danger : agent.error_count_last_run > 0 ? T.warning : T.success,
                      }}>
                        {agent.error_count_last_run}
                      </td>
                      <td style={{
                        fontSize: 12, padding: '10px 12px', borderBottom: `1px solid ${T.borderLight}`,
                        color: errorRate > 10 ? T.danger : errorRate > 5 ? T.warning : T.success,
                      }}>
                        {errorRate}%
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Bottom: Alerts + Score Summary */}
      <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: 16 }}>

        {/* Alerts Panel */}
        <div style={{ background: T.white, border: `1px solid ${T.border}`, borderRadius: T.radius, padding: 24, boxShadow: T.shadow }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
            <h3 style={{ fontSize: 11, fontWeight: 700, color: T.textLight, textTransform: 'uppercase', letterSpacing: '0.1em', margin: 0 }}>
              <span style={{ color: T.warning }}>&#9888;</span> Active Alerts
            </h3>
            {(swarmStatus?.alerts?.length ?? 0) > 0 && (
              <span style={{ fontSize: 10, fontWeight: 600, color: T.danger, background: T.dangerBg, padding: '2px 8px', borderRadius: 10 }}>
                {swarmStatus!.alerts.length} active
              </span>
            )}
          </div>

          {(swarmStatus?.alerts?.length ?? 0) > 0 ? swarmStatus!.alerts.slice(0, 5).map((alert, idx) => {
            const sevColor = alert.severity === 'critical' ? T.danger : alert.severity === 'warning' ? T.warning : T.navyLight;
            return (
              <div key={alert.id || idx} style={{
                display: 'flex', alignItems: 'flex-start', gap: 10,
                padding: '10px 12px', borderRadius: T.radiusSm,
                border: `1px solid ${T.borderLight}`, marginBottom: 8,
                transition: 'background 0.15s',
              }}
                onMouseEnter={e => { (e.currentTarget as HTMLDivElement).style.background = T.offWhite; }}
                onMouseLeave={e => { (e.currentTarget as HTMLDivElement).style.background = 'transparent'; }}
              >
                <div style={{ width: 8, height: 8, borderRadius: '50%', marginTop: 4, flexShrink: 0, background: sevColor }} />
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 12, fontWeight: 600, color: T.textDark }}>{alert.message}</div>
                </div>
                <div style={{ fontSize: 10, color: T.textLight, flexShrink: 0 }}>{formatTimeAgo(alert.fired_at)}</div>
              </div>
            );
          }) : (
            <div style={{ padding: 24, textAlign: 'center', color: T.textLight, fontSize: 13 }}>
              {overallStatus === 'offline'
                ? 'Alerts will appear once the Agent Swarm service is running.'
                : 'No active alerts \u2014 all agents operating normally.'
              }
            </div>
          )}
        </div>

        {/* API Budget — Today */}
        <div style={{ background: T.white, border: `1px solid ${T.border}`, borderRadius: T.radius, padding: 24, boxShadow: T.shadow }}>
          <h3 style={{ fontSize: 11, fontWeight: 700, color: T.textLight, textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: 20 }}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ verticalAlign: '-2px', marginRight: 6 }}>
              <path d="M12 2v20M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6" />
            </svg>
            API Budget \u2014 Today
          </h3>

          {budgetSummary && budgetSummary.apis.length > 0 ? budgetSummary.apis.map(api => {
            const used = api.total_requests;
            const limit = api.daily_limit ?? 0;
            const pct = limit > 0 ? Math.min((used / limit) * 100, 100) : 0;
            const barColor = pct >= 90 ? T.danger : pct >= 70 ? T.warning : T.success;
            return (
              <div key={api.api_name} style={{ marginBottom: 14 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 }}>
                  <span style={{ fontSize: 12, fontWeight: 600, color: T.textDark }}>{api.display_name}</span>
                  <span style={{ fontSize: 11, color: T.textLight, fontVariantNumeric: 'tabular-nums' }}>
                    {used.toLocaleString()} / {limit.toLocaleString()}
                  </span>
                </div>
                <div style={{ height: 8, borderRadius: 4, background: T.borderLight, overflow: 'hidden' }}>
                  <div style={{
                    width: `${pct}%`, height: '100%', borderRadius: 4,
                    background: barColor, transition: 'width 0.6s ease',
                  }} />
                </div>
              </div>
            );
          }) : (
            <div style={{ padding: 16, textAlign: 'center', color: T.textLight, fontSize: 12 }}>
              {overallStatus === 'offline'
                ? 'Budget data will appear once the Agent Swarm service is running.'
                : 'No API usage recorded today.'
              }
            </div>
          )}
        </div>
      </div>

      {/* Keyframe animations */}
      <style>{`
        @keyframes pulse {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.4; }
        }
        @keyframes spin {
          to { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
}
